import { useQueryClient } from '@tanstack/react-query';
import { FC, MouseEvent, useMemo, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { SubtitlesGenerationProject } from '@/types/resources';
import { api } from '@/utils/api';
import { SubsGenerationProjectVideoStatus } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import webVTT from '@/utils/webVtt';

import Card, { ActionButton } from './Card';
import ScriptField from './ScriptField';
import SubsPositionField from './SubsPositionField';
import TimestampField from './TimestampField';

const Video = styled.video`
  width: 100%;
  height: auto;
  max-height: 800px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  margin-bottom: 24px;

  /* &::-webkit-media-controls-panel {
    background-image: linear-gradient(transparent, transparent) !important;
  } */
`;

const Editor = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Column = styled.div`
  padding: 12px 24px;
`;

const TimestampsColumn = styled(Column)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 215px;
`;

const ScriptColumn = styled(Column)`
  flex: 1;
  border-left: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const ColumnTitle = styled(Text).attrs({ $color: 'gray', $type: 'button' })`
  margin-bottom: 8px;
  padding-top: 12px;
`;

const EditorFooterActions = styled.div`
  /* text-align: right; */
  padding-bottom: 12px;
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const Note = styled(Text)`
  display: flex;
  align-items: center;
  margin-top: 30px;
  svg {
    margin-right: 4px;
  }
`;

const ScriptFieldWrap = styled.div`
  display: flex;
  align-items: flex-start;
`;

const RegenerationButton = styled(ActionButton)`
  &:hover {
    color: ${({ theme }) => theme.colors.error};
    border-color: ${({ theme }) => theme.colors.error};
  }
`;

export type FormDataT = {
  script: Array<{
    timestamp: string;
    text: string;
    position: 'top' | 'bottom';
  }>;
};

interface Props {
  project: SubtitlesGenerationProject;
}

const SubtitlesEditorCard: FC<Props> = ({ project }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);

  const {
    control,
    handleSubmit,
    setFocus,
    setError,
    reset,
    clearErrors,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<FormDataT>({
    defaultValues: {
      script: project.script?.map((row) => ({
        ...row,
        position: row.position ?? 'bottom',
      })) ?? [
        {
          timestamp: '',
          text: '',
          position: 'bottom',
        },
      ],
    },
  });

  const { fields, insert, replace, remove } = useFieldArray({
    control,
    name: 'script',
  });

  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      const toastId = toasts.create('loading');

      try {
        await api.put(
          `/v1/distribution/subtitles-generation-projects/${project.id}`,
          data,
        );

        queryClient.setQueryData<SubtitlesGenerationProject>(
          [
            'subtitles_generation_projects',
            {
              id: project.id.toString(),
            },
          ],
          (oldData) => ({ ...oldData!, script: data.script }),
        );

        reset(data);

        toasts.success(toastId, 'Saved');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
        toasts.close(toastId);
      }
    })(e);
  };

  const onRegenerate = async () => {
    try {
      const confirmed = alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      const data = await api.post<object, FormDataT>(
        `/v1/distribution/subtitles-generation-projects/${project.id}/regenerate-script`,
      );

      queryClient.setQueryData<SubtitlesGenerationProject>(
        [
          'subtitles_generation_projects',
          {
            id: project.id.toString(),
          },
        ],
        (oldData) => ({ ...oldData!, script: data.script }),
      );

      reset(data);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const subtitlesSrc = useMemo(() => {
    if (!project.script?.length) {
      return null;
    }

    const arr = project.script.map((row) => {
      const timestamp = row.timestamp
        .split(' - ')
        .map((part, index) => part + (!index ? '.001' : '.000'))
        .join(' --> ');

      return {
        ...row,
        timestamp,
      };
    });

    const vtt = webVTT.fromArray(arr);

    return webVTT.toDataSrc(vtt);
  }, [project.script]);

  const isSaveButtonDisabled =
    !isDirty ||
    isSubmitting ||
    !isValid ||
    project.videoStatus === SubsGenerationProjectVideoStatus.GENERATING;

  return (
    <Card
      storageKey={`subtitles_generation_project_${project.id}_subtitles_visibility`}
      defaultIsOpened
      title="Original script with timestamps"
      description="Paste the script text, add timestamps, and use ChatGPT for structuring timestamps"
      actions={
        <ActionButton onClick={onSubmit} disabled={isSaveButtonDisabled}>
          Save
        </ActionButton>
      }
    >
      {!!project.video && (
        <Video controls ref={videoRef}>
          <source src={project.video.versions.original} type="video/mp4" />
          {!!subtitlesSrc && (
            <track
              label="English"
              kind="subtitles"
              srcLang="en"
              src={subtitlesSrc}
              default
            />
          )}
          Your browser does not support the video tag.
        </Video>
      )}
      <Editor>
        <Row>
          <TimestampsColumn>
            <ColumnTitle>Timestamps</ColumnTitle>
          </TimestampsColumn>
          <ScriptColumn>
            <ColumnTitle>Script</ColumnTitle>
          </ScriptColumn>
        </Row>
        {fields.map(({ id }, index) => (
          <Row key={id}>
            <TimestampsColumn>
              <TimestampField
                index={index}
                control={control}
                videoRef={videoRef}
              />
            </TimestampsColumn>
            <ScriptColumn>
              <ScriptFieldWrap>
                <ScriptField
                  index={index}
                  control={control}
                  replace={replace}
                  insert={insert}
                  setFocus={setFocus}
                  remove={remove}
                  fieldsCount={fields.length}
                />
                <SubsPositionField control={control} index={index} />
              </ScriptFieldWrap>
              {index + 1 === fields.length && (
                <>
                  <Note $type="smallNormal" $color="gray">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.6">
                        <circle cx="6" cy="6" r="6" fill="currentColor" />
                        <path
                          d="M5.4375 4.30147V3.375H6.5625V4.30147H5.4375ZM5.4375 8.625V4.91912H6.5625V8.625H5.4375Z"
                          fill="white"
                        />
                      </g>
                    </svg>
                    Hit enter to start new paragraph with timestamp
                  </Note>
                  <EditorFooterActions>
                    <ActionButton
                      onClick={onSubmit}
                      disabled={isSaveButtonDisabled}
                    >
                      Save
                    </ActionButton>
                    <RegenerationButton $secondary onClick={onRegenerate}>
                      Regenerate
                    </RegenerationButton>
                  </EditorFooterActions>
                </>
              )}
            </ScriptColumn>
          </Row>
        ))}
      </Editor>
    </Card>
  );
};

export default SubtitlesEditorCard;
